import React, {
  createContext, useContext, useReducer, useMemo,
} from 'react';
import PropTypes from 'prop-types';

const initialState = {
  filteredRows: [],
  selectedRows: [],
  disabledRows: [],
  searchFilter: '',
  selectedFilters: {},
  showOnlySelectedRows: false,
  resetPaginationToggle: false,
};

function TableReducer(state, action) {
  switch (action.type) {
    case 'LOAD_FILTERED_ROWS':
      return {
        ...state,
        filteredRows: action.rows,
      };

    case 'SELECT_ROW': {
      const selectedRows = action.checked
        ? [...state.selectedRows, action.rowId]
        : [...state.selectedRows.filter((rowId) => rowId !== action.rowId)];

      return {
        ...state,
        selectedRows,
      };
    }

    case 'DISABLE_ROW':
      return {
        ...state,
        disabledRows: [...state.disabledRows, action.rowId],
      };

    case 'SELECT_ALL_ROWS': {
      const rowsToSelect = state.filteredRows.filter((row) => !state.disabledRows.includes(row));

      return {
        ...state,
        selectedRows: [...new Set(state.selectedRows.concat(rowsToSelect))],
      };
    }

    case 'DESELECT_ALL_ROWS':
      return {
        ...state,
        selectedRows: [...new Set(state.selectedRows.filter((rowId) => !state.filteredRows.includes(rowId)))],
      };

    case 'SEARCH':
      return {
        ...state,
        searchFilter: action.value,
        resetPaginationToggle: !state.resetPaginationToggle,
      };

    case 'FILTER':
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [action.name]: action.value,
        },
        resetPaginationToggle: !state.resetPaginationToggle,
      };

    case 'SHOW_ONLY_SELECTED_ROWS':
      return {
        ...state,
        showOnlySelectedRows: action.value,
        resetPaginationToggle: !state.resetPaginationToggle,
      };

    case 'CLEAR_FILTERS':
      return {
        ...initialState,
        selectedRows: state.selectedRows,
        showOnlySelectedRows: state.showOnlySelectedRows,
        resetPaginationToggle: !state.resetPaginationToggle,
      };

    default:
      throw Error(`Unknown action: ${action.type}`);
  }
}

const TableContext = createContext({});

export function useTableContext() {
  return useContext(TableContext);
}

export function TableProvider({ children }) {
  const [state, dispatch] = useReducer(TableReducer, initialState);

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  );
}

TableProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
